import { BaseClient } from '../client/BaseClient';
import {
  Session,
  CreateSessionRequest,
  UpdateSessionRequest,
  ApiResponse,
} from '../types';

export class Sessions {
  constructor(private client: BaseClient) {}

  /**
   * List all sessions
   * @param environment - Filter by environment (optional)
   */
  async list(environment?: string): Promise<{ sessions: Session[]; count: number }> {
    const params = environment ? { environment } : undefined;
    const response = await this.client['get']<{
      success: boolean;
      sessions: Session[];
      count: number;
    }>('/api/sessions', { params });
    
    return {
      sessions: response.sessions,
      count: response.count,
    };
  }

  /**
   * Get session details
   * @param tel - Phone number
   */
  async get(tel: string): Promise<Session> {
    const response = await this.client['get']<{
      success: boolean;
      session: Session;
    }>(`/api/sessions/${encodeURIComponent(tel)}`);
    
    return response.session;
  }

  /**
   * Create a new session
   * @param data - Session creation data
   */
  async create(data: CreateSessionRequest): Promise<Session> {
    const response = await this.client['post']<{
      success: boolean;
      session: Session;
    }>('/api/sessions', data);
    
    return response.session;
  }

  /**
   * Update session details
   * @param tel - Phone number
   * @param data - Update data
   */
  async update(tel: string, data: UpdateSessionRequest): Promise<Session> {
    const response = await this.client['put']<{
      success: boolean;
      session: Session;
    }>(`/api/sessions/${encodeURIComponent(tel)}`, data);
    
    return response.session;
  }

  /**
   * Initialize session (start connection and generate QR code)
   * @param tel - Phone number
   */
  async initialize(tel: string): Promise<{ message: string }> {
    const response = await this.client['post']<ApiResponse<never>>(`/api/sessions/${encodeURIComponent(tel)}/init`);
    return { message: response.message || 'Session initialization started' };
  }


  /**
   * Reset session (disconnect and clear auth)
   * @param tel - Phone number
   */
  async reset(tel: string): Promise<{ message: string }> {
    const response = await this.client['post']<ApiResponse<never>>(`/api/sessions/${encodeURIComponent(tel)}/reset`);
    return { message: response.message || 'Session reset successfully' };
  }

  /**
   * Delete session permanently
   * @param tel - Phone number
   */
  async delete(tel: string): Promise<{ message: string }> {
    const response = await this.client['delete']<ApiResponse<never>>(`/api/sessions/${encodeURIComponent(tel)}`);
    return { message: response.message || 'Session deleted successfully' };
  }

  /**
   * Poll for session status (useful for checking QR code availability)
   * @param tel - Phone number
   * @param maxAttempts - Maximum polling attempts
   * @param interval - Polling interval in milliseconds
   */
  async pollForQR(
    tel: string,
    maxAttempts: number = 30,
    interval: number = 2000
  ): Promise<Session> {
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      const session = await this.get(tel);
      
      if (session.qr || session.ready) {
        return session;
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }
    
    throw new Error('Timeout waiting for QR code');
  }

  /**
   * Wait for session to be ready (connected)
   * @param tel - Phone number
   * @param maxAttempts - Maximum polling attempts
   * @param interval - Polling interval in milliseconds
   */
  async waitForReady(
    tel: string,
    maxAttempts: number = 60,
    interval: number = 2000
  ): Promise<Session> {
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      const session = await this.get(tel);
      
      if (session.ready) {
        return session;
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }
    
    throw new Error('Timeout waiting for session to be ready');
  }
}